/**
 * Klaviyo popup / teaser manager (theme-side).
 *
 * The Klaviyo signup form ("Unlock Your Mystery Discount") and its minimised
 * sticky teaser are injected by Klaviyo's hosted script, so they can't be edited
 * in the theme. This controller layers two behaviours on top of them:
 *
 *  1. "No, thanks" suppresses the sticky teaser for the rest of the session.
 *  2. The page-scroll lock Klaviyo applies while the popup is open is reliably
 *     released when the form closes (Klaviyo can leave it stuck — see below).
 *  3. While the Shopify cookie banner is visible, the teaser is lifted above it
 *     and the cookie banner is given stacking priority so the two never compete
 *     for the same bottom-of-screen space on mobile.
 *
 * Scroll lock: Klaviyo freezes the page (overflow/position on <html>/<body>)
 * while the popup is open and unlocks it as part of its close lifecycle. That
 * unlock can fail to run, leaving the page unscrollable. We listen for Klaviyo's
 * own `klaviyoForms` close/submit events and forcibly clear the lock ourselves.
 *
 * Selectors use the form-id-agnostic `[class*="kl-teaser-"]` /
 * `[class*="klaviyo-form-"]` patterns so a Klaviyo re-publish (new action ids)
 * doesn't break it.
 */
(() => {
  if (window.__klaviyoPopupManager) return;
  window.__klaviyoPopupManager = true;

  const DISMISS_KEY = 'kl_teaser_dismissed';
  const DISMISS_CLASS = 'kl-teaser-dismissed';
  const COOKIE_ACTIVE_CLASS = 'kl-cookie-active';
  const COOKIE_OFFSET_VAR = '--kl-cookie-offset';
  const KLAVIYO_ACTIVE_CLASS = 'kl-klaviyo-active';

  // Position-lock style props (bodyScrollLock-style freeze). Overflow is handled
  // separately because Klaviyo may lock via a CSS *class*, not an inline style —
  // so clearing inline alone isn't enough; we override with inline !important.
  // Does NOT touch custom props like --kl-cookie-offset.
  const POSITION_LOCK_PROPS = [
    'position', 'top', 'left', 'right', 'bottom', 'width', 'height', 'padding-right',
  ];

  const isDismissed = () => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch (e) {
      return false;
    }
  };

  const setDismissed = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch (e) {
      /* sessionStorage unavailable (private mode quota) – fail open */
    }
  };

  /* --------------------------------------------------------------------- *
   * Scroll-lock recovery
   * --------------------------------------------------------------------- */

  const unlockEl = (el) => {
    if (!el || !el.style) return;
    // Recover the scroll offset from a position:fixed-style lock.
    const fixedTop =
      el.style.position === 'fixed' ? parseInt(el.style.top || '0', 10) : 0;
    POSITION_LOCK_PROPS.forEach(p => el.style.removeProperty(p));
    // Klaviyo can lock scroll via a CSS class (overflow:hidden) that inline
    // clearing won't beat. If the element still resolves to non-scrollable,
    // force it with an inline !important override (highest author priority).
    // Re-reading computed style after our own change is self-terminating: once
    // it's no longer hidden we stop touching it.
    const oy = getComputedStyle(el).overflowY;
    if (oy === 'hidden' || oy === 'clip') {
      el.style.setProperty('overflow', 'auto', 'important');
    }
    if (fixedTop) window.scrollTo(0, -fixedTop);
  };

  const unlockScroll = () => {
    unlockEl(document.documentElement);
    unlockEl(document.body);
  };

  // Unlock now, then watch for ~2.5s in case Klaviyo (re)applies the lock
  // asynchronously after its close animation. Watches both `style` and `class`
  // because the lock may be class-based. Auto-disconnects so it can't interfere
  // with the theme's own scroll locks (cart drawer, menu) later.
  const releaseScrollLock = () => {
    unlockScroll();
    const observer = new MutationObserver(unlockScroll);
    const opts = { attributes: true, attributeFilter: ['style', 'class'] };
    observer.observe(document.documentElement, opts);
    observer.observe(document.body, opts);
    window.setTimeout(() => observer.disconnect(), 2500);
  };

  // Klaviyo dispatches `klaviyoForms` events on window for its form lifecycle.
  // Any close/submit means the popup is gone, so the page must be scrollable.
  window.addEventListener('klaviyoForms', (event) => {
    const type = event && event.detail && event.detail.type;
    if (type === 'close' || type === 'submit' || type === 'redirectedToUrl') {
      releaseScrollLock();
    }
    // Any lifecycle change can change what Klaviyo is showing — re-sync the chat
    // bubble now and again after the close/transition animation has settled.
    // syncChat re-reads the DOM, so it's self-correcting regardless of event type.
    if (typeof syncChat === 'function') {
      syncChat();
      window.setTimeout(syncChat, 600);
    }
  });

  /* --------------------------------------------------------------------- *
   * Hide the stuck overlay after the close animation
   * --------------------------------------------------------------------- */

  // Klaviyo fades the popup out with the `klaviyo-fadeout` animation and is
  // meant to set the dialog to display:none when it ends. That can fail,
  // leaving an invisible but full-screen fixed overlay that swallows every
  // click on the page. When the fade-out finishes, make sure the dialog is
  // actually hidden. Scoped to the fade-out animation only, so reopening the
  // popup (which uses `klaviyo-fadein`) is unaffected.
  document.addEventListener(
    'animationend',
    (event) => {
      if (event.animationName !== 'klaviyo-fadeout') return;
      const el = event.target;
      if (!(el instanceof Element)) return;
      const dialog = el.matches('[role="dialog"]')
        ? el
        : el.closest('[role="dialog"]');
      if (dialog) dialog.style.display = 'none';
      releaseScrollLock();
      // Popup has finished fading out → the chat bubble can reappear.
      syncChat();
    },
    true
  );

  /* --------------------------------------------------------------------- *
   * 1. Suppress the sticky teaser for the session on "No, thanks"
   * --------------------------------------------------------------------- */

  // Match the Klaviyo "No, thanks" button by its (normalised) label so it keeps
  // working if Klaviyo regenerates the data-action-id on the next publish.
  const isNoThanksButton = (el) => {
    const btn = el.closest('button.klaviyo-form-button, button[class*="klaviyo-form-button"]');
    if (!btn) return false;
    const label = (btn.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
    return label === 'no, thanks' || label === 'no thanks';
  };

  // The teaser's own close (X) is also a clear "not interested" signal.
  const isTeaserClose = (el) =>
    !!el.closest('[class*="kl-teaser-"] .klaviyo-close-form, [class*="kl-teaser-"] button[aria-label="Close teaser"]');

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      // "No, thanks" / teaser close → suppress the teaser for the session.
      if (isNoThanksButton(target) || isTeaserClose(target)) {
        setDismissed();
        document.body.classList.add(DISMISS_CLASS);
      }

      // Any Klaviyo close (the popup's X, the teaser's X, or "No, thanks")
      // dismisses the popup, so make sure the page scroll is freed — even if
      // the klaviyoForms close event doesn't fire for this interaction.
      if (
        target.closest('.klaviyo-close-form') ||
        isNoThanksButton(target) ||
        isTeaserClose(target)
      ) {
        releaseScrollLock();
        // Klaviyo is being dismissed → let the chat bubble come back, once now and
        // again after the popup's close animation has had time to finish.
        syncChat();
        window.setTimeout(syncChat, 600);
      }
    },
    true
  );

  /* --------------------------------------------------------------------- *
   * 2. Keep the teaser / popup from competing with the cookie banner
   * --------------------------------------------------------------------- */

  const cookieBanner = () => document.querySelector('cookie-banner');

  const syncCookieState = () => {
    const banner = cookieBanner();
    const visible = !!banner && banner.classList.contains('is-visible');

    document.body.classList.toggle(COOKIE_ACTIVE_CLASS, visible);

    if (visible) {
      // Lift the teaser to clear the banner's height plus a small gap.
      const offset = banner.offsetHeight + 12;
      document.documentElement.style.setProperty(COOKIE_OFFSET_VAR, offset + 'px');
    } else {
      document.documentElement.style.removeProperty(COOKIE_OFFSET_VAR);
    }
  };

  const observeCookieBanner = () => {
    const banner = cookieBanner();
    if (!banner) return;
    syncCookieState();
    new MutationObserver(syncCookieState).observe(banner, {
      attributes: true,
      attributeFilter: ['class'],
    });
    // Re-measure on resize (banner height changes with viewport width).
    window.addEventListener('resize', syncCookieState, { passive: true });
  };

  /* --------------------------------------------------------------------- *
   * 3. Don't show the chat bubble while the Klaviyo popup / teaser is up
   * --------------------------------------------------------------------- *
   *
   * The Chatbase "Chat with Suzanne" bubble and the Klaviyo "Unlock Your Mystery
   * Discount" teaser both pin to the bottom of the screen — showing both at once
   * feels cluttered. We keep the discount offer primary: while a Klaviyo teaser
   * or popup is visible, hide the chat bubble; once Klaviyo is handled (submitted)
   * or dismissed ("No, thanks" / close), the chat bubble returns.
   *
   * The hide lives in CSS (body.kl-klaviyo-active … in klaviyo-popup-manager.css);
   * here we just keep that body class in sync with what Klaviyo is actually
   * showing. Both widgets load asynchronously, so we re-sync on Klaviyo's
   * lifecycle events, on dismiss clicks, and when nodes are added/removed. */

  // Whether an element is actually on screen. Klaviyo keeps its surfaces in the
  // DOM and toggles them with display/opacity — and crucially it fades the popup
  // out via opacity on a *parent*, leaving the closed node in place. So we must
  // walk ancestors: an element inside a display:none / visibility:hidden /
  // opacity:0 wrapper counts as hidden even though its own style looks visible.
  // (Earlier we tried tracking popup state from klaviyoForms events instead, but
  // Klaviyo fires `open` on load without a matching `close`, which left the chat
  // bubble hidden forever after a refresh. Reading the DOM has no sticky state.)
  const isVisible = (el) => {
    if (!el || !el.isConnected) return false;
    for (let node = el; node && node.nodeType === 1; node = node.parentElement) {
      const cs = getComputedStyle(node);
      if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) {
        return false;
      }
    }
    const rect = el.getBoundingClientRect();
    return rect.width > 1 && rect.height > 1;
  };

  // Is any Klaviyo signup surface on screen — the sticky teaser OR the modal
  // popup? Both are read from the DOM with the ancestor-aware check above, which
  // the live diagnostic confirmed is reliable for each:
  //   - the teaser ([class*="kl-teaser-"]) is display:none once dismissed
  //     ("No, thanks" / teaser close X), so it stops counting as visible;
  //   - the popup ([data-testid="POPUP"]) collapses to a 0x0 box when closed.
  // So the chat bubble returns as soon as both are gone — no sticky state.
  const klaviyoVisible = () => {
    const surfaces = document.querySelectorAll('[class*="kl-teaser-"], [data-testid="POPUP"]');
    for (let i = 0; i < surfaces.length; i++) {
      const el = surfaces[i];
      // `[class*="kl-teaser-"]` is a substring match, so it also matches our own
      // `body.kl-teaser-dismissed` marker — a full-page element that always reads
      // visible and would pin the chat bubble hidden forever after a dismissal.
      // The real teaser/popup are Klaviyo-injected nodes, never the page root.
      if (el === document.body || el === document.documentElement) continue;
      if (isVisible(el)) return true;
    }
    return false;
  };

  // Hide the chat bubble while the Klaviyo teaser is visible or its popup is open.
  const syncChat = () => {
    document.body.classList.toggle(KLAVIYO_ACTIVE_CLASS, klaviyoVisible());
  };

  // Coalesce bursts of mutations into one sync per frame.
  let chatSyncQueued = false;
  const scheduleChatSync = () => {
    if (chatSyncQueued) return;
    chatSyncQueued = true;
    window.requestAnimationFrame(() => {
      chatSyncQueued = false;
      syncChat();
    });
  };

  const observeChatCoexistence = () => {
    // childList only (cheap): catches the teaser / popup / chat bubble being
    // injected or torn down. Display toggles (teaser → popup → closed) arrive via
    // the klaviyoForms, click and animationend hooks; the delayed syncs cover the
    // initial async appearance of each widget.
    new MutationObserver(scheduleChatSync).observe(document.body, {
      childList: true,
      subtree: true,
    });
    syncChat();
    [400, 1200, 3000].forEach((delay) => window.setTimeout(syncChat, delay));
  };

  /* --------------------------------------------------------------------- *
   * Init
   * --------------------------------------------------------------------- */

  const start = () => {
    // Re-apply the session suppression on subsequent page loads in the same tab.
    if (isDismissed()) document.body.classList.add(DISMISS_CLASS);
    observeCookieBanner();
    observeChatCoexistence();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
